import { Injectable, inject } from '@angular/core';
import { Option } from '@layouts/external-authenticated-layout/options';
import { RegistrationStatus } from '@modules/companies/types/company';
import {
  Activity,
  Barrel,
  Building2,
  Calendar,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Handshake,
  LayoutDashboard,
  List,
  PackageSearch,
  Ship,
  Boxes,
  ArrowLeftRight,
  Gavel,
  Award,
  FileCheck,
} from 'lucide-angular';
import { LocalStorageService } from 'app/storage/local-storage-service';

@Injectable({ providedIn: 'root' })
export class PortalMenuOptionsService {
  private localStorage = inject(LocalStorageService);

  getOmcManagementOptions(): Option[] {
    const registrationStatus = this.localStorage.getOmcRegistrationStatus();
    const isRegistered = registrationStatus === RegistrationStatus.REGISTERED;
    const isSuspended = registrationStatus === RegistrationStatus.SUSPENDED;
    const isExternalContractReview =
      registrationStatus === RegistrationStatus.EXTERNAL_CONTRACT_REVIEW;

    return [
      {
        title: 'Dashboard',
        icon: LayoutDashboard,
        route: '/external-services/omc-management/dashboard',
        isEnabled: true,
      },
      {
        title: 'My Applications',
        icon: Building2,
        route: '/external-services/omc-management/my-applications',
        isEnabled: true,
      },
      {
        title: 'Contracts',
        icon: FileText,
        route: '/external-services/omc-management/contracts',
        isEnabled: isRegistered || isExternalContractReview || isSuspended,
        permissions: ['ROLE_VIEW_OMC_CONTRACT'],
      },
      {
        title: 'Demand Management',
        icon: ClipboardList,
        route: '/external-services/omc-management/omc-demands',
        isEnabled: isRegistered || isSuspended,
        permissions: ['ROLE_VIEW_OMC_DEMAND'],
      },
      {
        title: 'Weekly Stock Report',
        icon: PackageSearch,
        route: '/external-services/omc-management/daily-omc-stock-report',
        isEnabled: isRegistered || isSuspended,
        permissions: ['ROLE_VIEW_OMC_STOCK_REPORT'],
      },
      {
        title: 'Terminal Agreements',
        icon: Handshake,
        route: '/external-services/omc-management/omc-terminal-agreements',
        isEnabled: isRegistered || isSuspended,
        permissions: ['ROLE_VIEW_OMC_TERMINAL_AGREEMENT'],
      },
      {
        title: 'BPS Calendar',
        icon: Calendar,
        route: '/external-services/omc-management/bps-calendar',
        isEnabled: isRegistered || isSuspended,
      },
      {
        title: 'Cargo Documents',
        icon: FileText,
        route: '/external-services/omc-management/cargo-documents',
        isEnabled: isRegistered || isSuspended,
        soon: true,
      },
      {
        title: 'Discharge Instructions',
        icon: FileText,
        route: '/external-services/omc-management/discharge-instructions',
        isEnabled: isRegistered || isSuspended,
        soon: true,
      },
      {
        title: 'Cargo Surrender',
        icon: ArrowLeftRight,
        route: '/external-services/omc-management/cargo-surrenders',
        isEnabled: isRegistered || isSuspended,
        soon: true,
      },
      {
        title: 'Reports',
        icon: FileSpreadsheet,
        isEnabled: isRegistered || isSuspended,
        permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
        children: [
          {
            title: 'Tanker Line Up',
            route: '/external-services/omc-management/reports/tankers-line-up',
            isEnabled: isRegistered || isSuspended,
            permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
          },
        ],
      },
    ];
  }

  getOmcRegistrationOptions(): Option[] {
    return [
      {
        title: 'Registration',
        icon: Building2,
        route: 'registration/omc-registration/register-omc',
        isEnabled: true,
      },
    ];
  }

  getSupplierManagementOptions(): Option[] {
    return [
      {
        title: 'Dashboard',
        icon: Building2,
        route: '/external-services/supplier-management',
        isEnabled: true,
        exact: true,
      },
      {
        title: 'My Applications',
        icon: List,
        route: '/external-services/supplier-management/my-applications',
        isEnabled: true,
        exact: true,
      },
      {
        title: 'Vessel Nominations',
        icon: Ship,
        route: '/external-services/supplier-management/vessel-nominations',
        isEnabled: true,
        permissions: ['ROLE_VIEW_VESSEL_NOMINATION'],
      },
      {
        title: 'Private Transit Cargo',
        icon: Boxes,
        route: '/external-services/supplier-management/private-transit-cargo',
        isEnabled: true,
        permissions: ['ROLE_VIEW_PRIVATE_TRANSIT_CARGO']
      },
      {
        title: 'Tenders & Bidding',
        icon: Gavel,
        isEnabled: true,
        expanded: true,
        soon:true,
        children: [
          {
            title: 'Tender Listing',
            icon: List,
            route: '/external-services/supplier-management/tenders',
            isEnabled: true,
            exact: true,
          },
          {
            title: 'Tender Documents',
            icon: FileText,
            route: '/external-services/supplier-management/tenders/tender-documents',
            isEnabled: true,
          },
          {
            title: 'My Bids',
            icon: FileCheck,
            route: '/external-services/supplier-management/tenders/my-bids',
            isEnabled: true,
          },
          {
            title: 'Awards',
            icon: Award,
            route: '/external-services/supplier-management/tenders/awards',
            isEnabled: true,
          },
        ],
      },
      {
        title: 'Cargo Documents',
        icon: FileText,
        route: '/external-services/supplier-management/cargo-documents',
        isEnabled: true,
        soon: true,
      },
      {
        title: 'Discharge Instructions',
        icon: FileText,
        route: '/external-services/supplier-management/discharge-instructions',
        isEnabled: true,
        soon: true,
      },
      {
        title: 'Reports',
        icon: FileSpreadsheet,
        isEnabled: true,
        permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
        children: [
          {
            title: 'Tanker Line Up',
            route: '/external-services/supplier-management/reports/tankers-line-up',
            isEnabled: true,
            permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
          },
        ],
      },
    ];
  }

  getSupplierRegistrationOptions(): Option[] {
    return [
      {
        title: 'Supplier Registration',
        icon: Building2,
        route: 'registration/supplier-registration/register-supplier',
        isEnabled: true,
      },
    ];
  }

  getTerminalManagementOptions(): Option[] {
    return [
      {
        title: 'Dashboard',
        icon: LayoutDashboard,
        route: '/external-services/terminal-operator-management/terminal-operator-dashboard',
        isEnabled: true,
      },
      {
        title: 'Terminals',
        icon: Barrel,
        route: '/external-services/terminal-operator-management/terminals',
        isEnabled: true,
        permissions: ['ROLE_VIEW_TERMINAL_APPLICATION'],
      },
      {
        title: 'Daily Terminal Stock Reporting',
        icon: Activity,
        route: '/external-services/terminal-operator-management/daily-terminal-stock-reporting',
        isEnabled: true,
        permissions: ['ROLE_VIEW_DAILY_TERMINAL_STOCK', 'ROLE_VIEW_TERMINAL_STOCK'],
      },
      {
        title: 'Reports',
        icon: FileSpreadsheet,
        isEnabled: true,
        permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
        children: [
          {
            title: 'Tanker Line Up',
            route: '/external-services/terminal-operator-management/reports/tankers-line-up',
            isEnabled: true,
            permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
          },
        ],
      },
    ];
  }

  getTerminalRegistrationOptions(): Option[] {
    return [
      {
        title: 'Registration',
        icon: Building2,
        route: 'registration/terminal-registration/register-terminal',
        isEnabled: true,
      },
    ];
  }

  getNonBpsManagementOptions(): Option[] {
    const isNonBpsApproved = this.localStorage.getNonBpsStatus() === RegistrationStatus.APPROVED;

    return [
      {
        title: 'Dashboard',
        icon: LayoutDashboard,
        route: '/external-services/non-bps-management/dashboard',
        isEnabled: true,
        exact: true,
      },
      {
        title: 'My Applications',
        icon: List,
        route: '/external-services/non-bps-management/my-applications',
        isEnabled: true,
      },
      {
        title: 'Discharge Window Request(s)',
        icon: ClipboardList,
        route: '/external-services/non-bps-management/vessel-product-request',
        isEnabled: isNonBpsApproved,
        permissions: ['ROLE_REGISTRATION_COMPANY_REGISTER'],
      },
      {
        title: 'Reports',
        icon: FileSpreadsheet,
        isEnabled: isNonBpsApproved,
        permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
        children: [
          {
            title: 'Tanker Line Up',
            route: '/external-services/non-bps-management/reports/tankers-line-up',
            isEnabled: true,
            permissions: ['ROLE_VIEW_VESSEL_LINEUP'],
          },
        ],
      },
    ];
  }

  getNonBpsRegistrationOptions(): Option[] {
    return [
      {
        title: 'Registration',
        icon: Building2,
        route: 'registration/non-bps-registration/register-non-bps',
        isEnabled: true,
      },
    ];
  }

  getShippingAgentRegistrationOptions(): Option[] {
    return [
      {
        title: 'Registration',
        icon: Building2,
        route: 'registration/shipping-agent-registration/register-shipping-agent',
        isEnabled: true,
      },
    ];
  }

  getShippingAgentManagementOptions(): Option[] {
    return [
      {
        title: 'Dashboard',
        icon: LayoutDashboard,
        route: '/external-services/shipping-agent-management/dashboard',
        isEnabled: true,
      },
      {
        title: 'My Applications',
        icon: List,
        route: '/external-services/shipping-agent-management/my-applications',
        isEnabled: true,
        permissions: ['ROLE_VIEW_AGENT_APPLICATION'],
      },
      {
        title: 'Vessel Line-Up',
        icon: Ship,
        route: '/external-services/shipping-agent-management/vessel-line-up',
        isEnabled: true,
        soon: true,
      },
      {
        title: 'Discharge Instructions & Manifest',
        icon: FileSpreadsheet,
        route: '/external-services/shipping-agent-management/discharge-instructions',
        isEnabled: true,
        soon: true,
      },
    ];
  }
}

